import { AnimatePresence, motion } from 'framer-motion';
import * as React from 'react';
import { Cross } from '@sravni/react-icons';
import { useStyles, preventBodyScroll, useKeyPress, noop } from '@sravni/react-utils';
import { useBackwardEvent } from '../../../hooks/useBackwardEvent.js';
import { Icon } from '../../Icon/src/Icon.js';
import { Portal } from '../../Portal/src/Portal.js';
import { EASINGS } from '../../Transition/src/_utils.js';
import '../../Transition/src/Fade.js';
import { scaleFadeConfig } from '../../Transition/src/ScaleFade.js';
import '../../Transition/src/SlideFade.js';
import '../../Transition/src/Collapse.js';
import modules_74c5b1b9 from '../styles/Dialog.module.less.js';
import DialogContext from './DialogContext.js';

/**
 * @description The Dialog component is used to show content on top of an overlay that requires user interaction
 *
 * @component
 * @example
 * ```jsx
 * <Dialog title="Title">content</Dialog>
 * ```
 */
const Dialog = React.forwardRef((props, ref) => {
    const { visible, fullscreen, closable, closeOnBackward = false, className, children, onClose = noop, ...restProps } = props;
    const cx = useStyles(modules_74c5b1b9);
    const isUnmountEvent = React.useRef(true);
    // 'isActive' used to fix the problem of simultaneously exiting with Esc in nested dialogs
    const [isActive, setIsActive] = React.useState(true);
    const { setIsActivePreviousDialog } = React.useContext(DialogContext);
    const animationProps = React.useMemo(() => ({ initialScale: fullscreen ? 1.05 : 0.95, reverse: true }), [fullscreen]);
    const dialogMotionProps = React.useMemo(() => ({
        ...scaleFadeConfig,
        custom: animationProps,
        animate: visible ? 'enter' : 'exit',
    }), [animationProps, visible]);
    const backdropMotionProps = React.useMemo(() => ({
        variants: {
            exit: {
                opacity: 0,
                transition: {
                    duration: 0.15,
                    ease: EASINGS.easeOut,
                },
            },
            enter: {
                opacity: 0.6,
                transition: {
                    duration: 0.2,
                    ease: EASINGS.easeIn,
                },
            },
        },
        animate: visible ? 'enter' : 'exit',
        initial: 'exit',
        exit: 'exit',
    }), [visible]);
    const dialogContextProviderValue = React.useMemo(() => ({ fullscreen, setIsActivePreviousDialog: setIsActive }), [fullscreen]);
    const handleClose = React.useCallback((event) => {
        preventBodyScroll();
        setIsActivePreviousDialog?.(true);
        onClose?.(event);
        isUnmountEvent.current = false;
    }, [onClose, setIsActivePreviousDialog]);
    const handlePreventBodyScroll = React.useCallback(() => {
        if (visible && isUnmountEvent.current) {
            preventBodyScroll();
        }
    }, [visible, isUnmountEvent]);
    React.useEffect(() => {
        if (visible) {
            preventBodyScroll(true);
            setIsActivePreviousDialog?.(false);
            isUnmountEvent.current = true;
        }
        else {
            setIsActivePreviousDialog?.(true);
        }
        return handlePreventBodyScroll;
    }, [handlePreventBodyScroll, setIsActivePreviousDialog, visible]);
    useKeyPress({
        enabled: !!visible,
        keyMap: {
            Escape: (event) => {
                event.preventDefault();
                if (!isActive) {
                    return;
                }
                setIsActivePreviousDialog?.(true);
                handleClose(event);
            },
        },
    }, [visible, isActive]);
    const { closeEventHandler } = useBackwardEvent(closeOnBackward, !!visible, handleClose);
    return (React.createElement(Portal, null,
        React.createElement(AnimatePresence, { custom: animationProps, onExitComplete: closeEventHandler }, visible && (React.createElement("div", { className: cx('overlay') },
            !fullscreen && React.createElement(motion.div, { ...backdropMotionProps, className: cx('backdrop'), onClick: handleClose }),
            React.createElement(motion.div, { ...dialogMotionProps, ref: ref, "data-qa": "Dialog", className: cx(className, 'container', { fullscreen }), ...restProps },
                closable && React.createElement(Icon, { className: cx('close'), size: 28, icon: React.createElement(Cross, null), onClick: handleClose }),
                React.createElement(DialogContext.Provider, { value: dialogContextProviderValue }, children)))))));
});
Dialog.defaultProps = {
    closable: true,
    closeOnBackward: false,
};
Dialog.displayName = 'Dialog';

export { Dialog };
//# sourceMappingURL=Dialog.js.map
